/**
 * "Ajustes de la tienda" en /admin.
 *
 * Existe para que la dueña no dependa del desarrollador durante una campaña. El
 * porcentaje y las piezas mínimas cambian con la promoción del mes; si vivieran en
 * el código, cada cambio sería un deploy y una espera.
 *
 * El interruptor de los links de Clip **no borra nada**: los links siguen
 * guardados en cada producto y vuelven apenas se prende. Se dice en pantalla,
 * porque "apagar" suena a "perder" y eso frena a cualquiera.
 */

import { useEffect, useState } from 'react';
import * as api from '@/lib/api';

const LIMITES = { piezas: { min: 2, max: 50 }, porcentaje: { min: 1, max: 50 } };
const MAX_TRAMOS = 5;

/** Mismas reglas que valida el servidor, para avisar antes del viaje. La que
 *  importa: a más piezas, más descuento. Un tramo de 6 que descuenta menos que el
 *  de 3 castiga a quien compra más, y es el dedazo típico al teclear rápido. */
function revisarTramos(tramos) {
  if (!tramos.length) return 'Tiene que haber al menos un tramo.';
  for (const t of tramos) {
    if (!Number.isInteger(t.minUnits) || t.minUnits < LIMITES.piezas.min || t.minUnits > LIMITES.piezas.max) {
      return `Las piezas van de ${LIMITES.piezas.min} a ${LIMITES.piezas.max}.`;
    }
    if (!Number.isInteger(t.percent) || t.percent < LIMITES.porcentaje.min || t.percent > LIMITES.porcentaje.max) {
      return `El descuento va de ${LIMITES.porcentaje.min}% a ${LIMITES.porcentaje.max}%.`;
    }
  }
  const orden = [...tramos].sort((a, b) => a.minUnits - b.minUnits);
  for (let i = 1; i < orden.length; i++) {
    if (orden[i].minUnits === orden[i - 1].minUnits) {
      return `Hay dos tramos que arrancan en ${orden[i].minUnits} piezas.`;
    }
    if (orden[i].percent <= orden[i - 1].percent) {
      return `El tramo de ${orden[i].minUnits} piezas descuenta ${orden[i].percent}%, que no es más que el de ${orden[i - 1].minUnits} piezas (${orden[i - 1].percent}%). A más piezas, más descuento.`;
    }
  }
  return null;
}

/** "3+ piezas: 5% · 6+ piezas: 10%" — lo mismo que ve el cliente en el sitio. */
const resumirTramos = (tramos) =>
  (tramos || []).map((t) => `${t.minUnits}+ piezas: ${t.percent}%`).join(' · ');

function Interruptor({ id, activo, onCambiar, disabled, titulo, detalle }) {
  return (
    <label
      className="flex items-start gap-3 cursor-pointer select-none"
      data-testid={`ajuste-${id}`}
    >
      <input
        type="checkbox"
        checked={activo}
        disabled={disabled}
        onChange={(e) => onCambiar(e.target.checked)}
        className="mt-1 w-4 h-4 accent-[#d6ad60]"
        data-testid={`ajuste-${id}-input`}
      />
      <span>
        <span className="text-sm text-neutral-200">{titulo}</span>
        <span className="block text-xs text-neutral-500 mt-0.5 leading-relaxed">{detalle}</span>
      </span>
    </label>
  );
}

/**
 * Editor de tramos. Se trabaja sobre un borrador y se guarda con un botón, al revés
 * que los interruptores: acá un cambio a medias (el 10% tecleado pero todavía no las
 * 6 piezas) es un estado inválido que no tiene por qué llegar a la base.
 */
function Tramos({ tramos, ocupado, onGuardar }) {
  const [borrador, setBorrador] = useState(tramos);
  const [avisoLocal, setAvisoLocal] = useState(null);

  // Si el servidor confirma otros valores (otra pestaña, un rechazo), el borrador se
  // alinea con la verdad en vez de quedar mostrando lo que se tecleó.
  useEffect(() => {
    setBorrador(tramos);
    setAvisoLocal(null);
  }, [tramos]);

  const sucio = JSON.stringify(borrador) !== JSON.stringify(tramos);

  const cambiar = (i, campo, valor) =>
    setBorrador((prev) =>
      prev.map((t, j) => (j === i ? { ...t, [campo]: valor === '' ? '' : Number(valor) } : t))
    );

  const guardar = () => {
    const limpios = borrador
      .map((t) => ({ minUnits: Number(t.minUnits), percent: Number(t.percent) }))
      .sort((a, b) => a.minUnits - b.minUnits);
    const problema = revisarTramos(limpios);
    if (problema) return setAvisoLocal(problema);
    setAvisoLocal(null);
    onGuardar(limpios);
  };

  return (
    <div className="pl-7 space-y-3" data-testid="tramos">
      {borrador.map((t, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`tramo-${i}`}>
          <span className="text-xs text-neutral-500">Desde</span>
          <input
            type="number"
            min={LIMITES.piezas.min}
            max={LIMITES.piezas.max}
            value={t.minUnits}
            disabled={ocupado}
            onChange={(e) => cambiar(i, 'minUnits', e.target.value)}
            className="w-16 bg-[#0a0a0a] border border-[#1a1a1a] px-2 py-1.5 text-sm focus:border-[#d6ad60] outline-none"
            data-testid={`tramo-${i}-piezas`}
          />
          <span className="text-xs text-neutral-500">piezas →</span>
          <input
            type="number"
            min={LIMITES.porcentaje.min}
            max={LIMITES.porcentaje.max}
            value={t.percent}
            disabled={ocupado}
            onChange={(e) => cambiar(i, 'percent', e.target.value)}
            className="w-16 bg-[#0a0a0a] border border-[#1a1a1a] px-2 py-1.5 text-sm focus:border-[#d6ad60] outline-none"
            data-testid={`tramo-${i}-porcentaje`}
          />
          <span className="text-xs text-neutral-500">%</span>
          {borrador.length > 1 && (
            <button
              type="button"
              disabled={ocupado}
              onClick={() => setBorrador((prev) => prev.filter((_, j) => j !== i))}
              aria-label={`Quitar el tramo de ${t.minUnits} piezas`}
              className="ml-auto text-neutral-600 hover:text-neutral-300 text-xs"
              data-testid={`tramo-${i}-quitar`}
            >
              Quitar
            </button>
          )}
        </div>
      ))}

      {avisoLocal && (
        <p className="text-xs text-red-300" data-testid="tramos-aviso">
          {avisoLocal}
        </p>
      )}

      <div className="flex items-center gap-3 pt-1">
        {borrador.length < MAX_TRAMOS && (
          <button
            type="button"
            disabled={ocupado}
            onClick={() =>
              setBorrador((prev) => {
                const ultimo = prev[prev.length - 1] || { minUnits: 2, percent: 5 };
                return [
                  ...prev,
                  {
                    minUnits: Math.min(Number(ultimo.minUnits) * 2 || 6, LIMITES.piezas.max),
                    percent: Math.min(Number(ultimo.percent) * 2 || 10, LIMITES.porcentaje.max),
                  },
                ];
              })
            }
            className="text-xs uppercase tracking-widest text-[#d6ad60] hover:text-[#e5c37a]"
            data-testid="tramo-agregar"
          >
            + Agregar tramo
          </button>
        )}
        <button
          type="button"
          disabled={ocupado || !sucio}
          onClick={guardar}
          className="ml-auto border border-[#d6ad60] px-4 py-1.5 text-xs uppercase tracking-widest text-[#d6ad60] hover:bg-[#d6ad60] hover:text-black transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#d6ad60]"
          data-testid="tramos-guardar"
        >
          Guardar tramos
        </button>
      </div>
      <p className="text-[11px] text-neutral-600 leading-relaxed">
        El cliente recibe el descuento del tramo más alto que alcance. Máximo{' '}
        {MAX_TRAMOS} tramos, y a más piezas tiene que haber más descuento.
      </p>
    </div>
  );
}

export default function AjustesTienda() {
  const [ajustes, setAjustes] = useState(null);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [abierto, setAbierto] = useState(false);

  useEffect(() => {
    let vivo = true;
    api
      .leerAjustes()
      .then((d) => vivo && setAjustes(d.settings))
      .catch((e) => vivo && setError(e.message));
    return () => {
      vivo = false;
    };
  }, []);

  /** Guarda y aplica lo que confirmó el servidor, no lo que se tecleó: si el
   *  servidor corrige o rechaza, la pantalla muestra la verdad. */
  async function guardar(cambios) {
    setOcupado(true);
    setError(null);
    setAviso(null);
    try {
      const { settings } = await api.guardarAjustes(cambios);
      setAjustes(settings);
      setAviso('Ajustes guardados.');
      setTimeout(() => setAviso(null), 2500);
    } catch (e) {
      setError(e.message);
      // Se recarga lo que hay de verdad en la base: dejar la pantalla con el valor
      // que falló haría creer que quedó guardado.
      api
        .leerAjustes()
        .then((d) => {
          setAjustes(d.settings);
            })
        .catch(() => {});
    } finally {
      setOcupado(false);
    }
  }

  if (error && !ajustes) {
    return (
      <p className="mb-6 border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
        No se pudieron cargar los ajustes: {error}
      </p>
    );
  }
  if (!ajustes) return null;

  return (
    <section className="mb-6 border border-[#1a1a1a]" data-testid="ajustes-tienda">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
        data-testid="ajustes-toggle"
      >
        <span>
          <span className="text-sm uppercase tracking-widest text-neutral-300">
            Ajustes de la tienda
          </span>
          <span className="block text-xs text-neutral-500 mt-0.5">
            {ajustes.promoEnabled ? resumirTramos(ajustes.promoTiers) : 'Promoción apagada'}
            {' · '}
            {ajustes.payLinksEnabled ? 'Links de pago visibles' : 'Links de pago ocultos'}
          </span>
        </span>
        <span className="text-[#d6ad60] text-lg leading-none">{abierto ? '−' : '+'}</span>
      </button>

      {abierto && (
        <div className="px-5 pb-5 space-y-5 border-t border-[#1a1a1a] pt-5">
          {aviso && <p className="text-xs text-[#d6ad60]" data-testid="ajustes-aviso">{aviso}</p>}
          {error && <p className="text-xs text-red-300" data-testid="ajustes-error">{error}</p>}

          <Interruptor
            id="promo"
            activo={ajustes.promoEnabled}
            disabled={ocupado}
            onCambiar={(v) => guardar({ promoEnabled: v })}
            titulo="Descuento por varias piezas"
            detalle="Se aplica sólo en los pedidos que llegan por WhatsApp desde el carrito. El sitio anuncia la condición en la colección."
          />

          {ajustes.promoEnabled && (
            <Tramos
              tramos={ajustes.promoTiers}
              ocupado={ocupado}
              onGuardar={(promoTiers) => guardar({ promoTiers })}
            />
          )}

          <Interruptor
            id="paylinks"
            activo={ajustes.payLinksEnabled}
            disabled={ocupado}
            onCambiar={(v) => guardar({ payLinksEnabled: v })}
            titulo="Mostrar los botones “Comprar ahora” (Clip)"
            detalle="Al apagarlo, el sitio deja de mostrar los links de pago y todos los pedidos pasan por WhatsApp. Los links NO se borran: siguen guardados en cada fragancia y vuelven al prenderlo. Ojo: quien paga por Clip paga el precio completo, el descuento por piezas sólo existe por WhatsApp."
          />
        </div>
      )}
    </section>
  );
}
